/* ==========================================================================
   K+K Lead Time — assets/kk-lead-time.js

   Unified build. One container, one data block, one script. Handles both:

     • Native Shopify variants  — reads variant metafields from the snippet's
       JSON block, follows the cart form's id input and the variant URL param.
     • Optis (BSS_PO) options   — reads the customer's selected option text and
       matches it against the keyword rules in G_IS / G_OV / P_NONE below.

   On any product the two are combined and WHICHEVER IS LONGER WINS. On a
   product with no Optis options the option side simply never contributes.

   An arbiter at the bottom decides which provider owns the page before the
   first paint, so there is no flash of the wrong number. Native pages are
   event-driven only — the 300ms poll runs on Optis pages, where it is the
   only way to see a BSS_PO selection change.

   Touches nothing outside its own [data-kk-lt] container. Does not wrap fetch.
   Requires no Liquid or CSS changes — same markup, same class names.
   ========================================================================== */

(function () {
  'use strict';

  /* ======================================================================
     1. CONFIG
     ====================================================================== */

  var CONFIG = {
    /* Shown when nothing else resolves — matches the old builder's DEFAULT. */
    defaultMin: 4,
    defaultMax: 6,

    /* Which Optis option to read.
       null = evaluate every selected option, longest wins (recommended —
              on a product where size, mount and colour are all Optis options,
              all three get a say).
       0    = first option only, the old kk-lead-optis.js behaviour. */
    optionIndex: null,

    /* true  = show "Lead time depends on the options you choose" until the
               customer actively changes a selection (old Optis behaviour).
       false = answer immediately from whatever is preselected. */
    requireInteraction: false,

    /* How long to wait for BSS_PO before giving up and rendering native. */
    optisWaitMs: 800,

    /* Poll interval on Optis pages only. */
    pollMs: 300,

    /* DOM fingerprints that say "this product probably has Optis options",
       checked synchronously before BSS_PO exists. If Optis products ever
       render the native block for ~800ms before correcting themselves, the
       real container class is missing from this list — add it. */
    optisMarkers: [
      '#bss-po-app',
      '.bss-po-container',
      '.bss_po_container',
      '[class^="bss-po"]',
      '[class*=" bss-po"]',
      '[id^="bss-po"]'
    ]
  };

  /* ======================================================================
     2. OPTION RULES  — paste from kk-lead-time-builder_final_v2.html
     ====================================================================== */

  /* Option text containing any of these → treated as in stock. */
  var G_IS = [
    /* 'No Bracket',
       'Standard Mount' */
  ];

  /* Option text containing [0] → lead time [1]. */
  var G_OV = [
    /* ['Custom Color', '6-8 weeks'],
       ['Chrome Housing', '3-5 weeks'] */
  ];

  /* "None" selected, per product handle.
     Value = 'instock' | 'default' | a lead time string like '3-5 weeks'.
     A handle with no entry means "None" falls back to the base variant. */
  var P_NONE = {
    /* 'federal-signal-micropulse-ultra-mps6u': 'instock' */
  };

  /* ======================================================================
     3. COPY
     ====================================================================== */

  var COPY = {
    stockEyebrow: 'In stock',
    stockHeadline: 'Ships in 1\u20132 business days',
    stockDetail: 'Tracking sent as soon as it leaves the shop.',
    orderEyebrow: 'Built to order',
    orderDetail: 'Tracking sent when it ships.',
    pendingEyebrow: 'Lead time',
    pendingHeadline: 'Ship date depends on the options you choose',
    pendingDetail: 'Select your options to see the window.'
  };

  /* ======================================================================
     4. DOM
     ====================================================================== */

  var root = document.querySelector('[data-kk-lt]');
  if (!root) return;

  var dataEl = root.querySelector('[data-kk-lt-data]');
  var eyebrowEl = root.querySelector('[data-kk-lt-eyebrow]');
  var headlineEl = root.querySelector('[data-kk-lt-headline]');
  var detailEl = root.querySelector('[data-kk-lt-detail]');
  if (!dataEl || !headlineEl) return;

  var VARIANTS;
  try {
    VARIANTS = JSON.parse(dataEl.textContent).variants || {};
  } catch (e) {
    return; // bad JSON: leave the server-rendered fallback in place
  }

  /* ======================================================================
     5. RESULTS
     A result is one of:
       { kind: 'stock' }
       { kind: 'weeks', min: n, max: n }
       { kind: 'pending' }   nothing resolvable yet
     ====================================================================== */

  function stockResult() { return { kind: 'stock' }; }
  function weeksResult(min, max) { return { kind: 'weeks', min: min, max: max }; }
  function pendingResult() { return { kind: 'pending' }; }
  function defaultResult() { return weeksResult(CONFIG.defaultMin, CONFIG.defaultMax); }

  /* Comparable upper bound in weeks. In stock is 0. Pending loses to everything. */
  function weight(r) {
    if (!r) return -1;
    if (r.kind === 'stock') return 0;
    if (r.kind === 'weeks') return r.max;
    return -1;
  }

  function longer(a, b) {
    if (!a) return b;
    if (!b) return a;
    return weight(b) > weight(a) ? b : a;
  }

  /* "4-6 weeks" → 4/6.  "2 weeks" → 2/2.  "Ships within 1-2 business days" → stock. */
  function parseLead(text) {
    if (!text) return pendingResult();
    if (/business day|in stock|ready to ship|ships within/i.test(text)) return stockResult();
    var m = text.match(/(\d+)\s*(?:[-\u2013]|to)\s*(\d+)\s*weeks?/i);
    if (m) return weeksResult(parseInt(m[1], 10), parseInt(m[2], 10));
    m = text.match(/(\d+)\s*weeks?/i);
    if (m) return weeksResult(parseInt(m[1], 10), parseInt(m[1], 10));
    return pendingResult();
  }

  /* ======================================================================
     6. NATIVE SIDE
     ====================================================================== */

  function currentId() {
    /* Form input first — during a variant change the URL can lag it by a tick. */
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return input.value;
    return new URLSearchParams(window.location.search).get('variant');
  }

  function baseResult(id) {
    var v = id ? VARIANTS[id] : null;
    if (!v) return pendingResult();
    if (v.stock) return stockResult();
    if (v.min !== null && v.min !== undefined &&
        v.max !== null && v.max !== undefined) {
      return weeksResult(v.min, v.max);
    }
    return pendingResult();
  }

  /* ======================================================================
     7. OPTIS SIDE
     ====================================================================== */

  function productHandle() {
    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : '';
  }

  function selectedValues() {
    if (typeof BSS_PO === 'undefined') return null;
    try {
      var c = BSS_PO.currentSelectedOptions;
      return (c && c.allValues && c.allValues.length) ? c.allValues : null;
    } catch (e) {
      return null;
    }
  }

  /* What one option value asks for. null = no contribution, defer to the base. */
  function ruleFor(text) {
    if (!text) return null;

    if (text.indexOf('None') !== -1) {
      var rule = P_NONE[productHandle()];
      if (rule === 'instock') return stockResult();
      if (rule === 'default') return defaultResult();
      if (rule) return parseLead(rule);
      return null; // no product rule: "None" means just the base product
    }

    var i;
    for (i = 0; i < G_IS.length; i++) {
      if (text.indexOf(G_IS[i]) !== -1) return stockResult();
    }
    for (i = 0; i < G_OV.length; i++) {
      if (text.indexOf(G_OV[i][0]) !== -1) return parseLead(G_OV[i][1]);
    }
    return defaultResult();
  }

  function optionsResult(values) {
    if (!values || !values.length) return null;

    if (typeof CONFIG.optionIndex === 'number') {
      var one = values[CONFIG.optionIndex];
      return ruleFor(one && one.value);
    }

    var best = null;
    for (var i = 0; i < values.length; i++) {
      var r = ruleFor(values[i] && values[i].value);
      if (r) best = longer(best, r);
    }
    return best;
  }

  function valuesSignature(values) {
    if (!values) return '';
    var out = [];
    for (var i = 0; i < values.length; i++) {
      out.push((values[i] && values[i].value) || '');
    }
    return out.join('\u0001');
  }

  /* ======================================================================
     8. PAINT
     ====================================================================== */

  var painted = null;

  function textFor(result) {
    if (result.kind === 'stock') {
      return {
        ready: true,
        eyebrow: COPY.stockEyebrow,
        headline: COPY.stockHeadline,
        detail: COPY.stockDetail
      };
    }
    if (result.kind === 'weeks') {
      return {
        ready: false,
        eyebrow: COPY.orderEyebrow,
        headline: 'Ships in ' +
          (result.min === result.max ? result.min : result.min + '\u2013' + result.max) +
          ' weeks',
        detail: COPY.orderDetail
      };
    }
    return {
      ready: false,
      eyebrow: COPY.pendingEyebrow,
      headline: COPY.pendingHeadline,
      detail: COPY.pendingDetail
    };
  }

  function paint(result) {
    var out = textFor(result);
    var sig = out.eyebrow + '|' + out.headline + '|' + out.detail;

    if (sig === painted) {
      root.classList.remove('is-swapping');
      return;
    }

    var first = painted === null;
    painted = sig;

    if (!first) root.classList.add('is-swapping');

    window.requestAnimationFrame(function () {
      if (out.ready) root.classList.add('kk-lt--ready');
      else root.classList.remove('kk-lt--ready');

      if (eyebrowEl) eyebrowEl.textContent = out.eyebrow;
      headlineEl.textContent = out.headline;
      if (detailEl) detailEl.textContent = out.detail;

      window.requestAnimationFrame(function () {
        root.classList.remove('is-swapping');
      });
    });
  }

  /* ======================================================================
     9. PROVIDERS
     ====================================================================== */

  function nativeSync() {
    var id = currentId();
    var r = baseResult(id);
    if (r.kind === 'pending' && id) r = defaultResult();
    paint(r);
  }

  var baselineSig = null;
  var interactionSeen = false;

  function optisSync() {
    var values = selectedValues();
    var sig = valuesSignature(values);

    if (CONFIG.requireInteraction) {
      if (baselineSig === null) baselineSig = sig;
      else if (sig !== baselineSig) interactionSeen = true;
      if (!interactionSeen) { paint(pendingResult()); return; }
    }

    var base = baseResult(currentId());
    var opt = optionsResult(values);

    var out = opt ? longer(base, opt) : base;
    if (out.kind === 'pending' && (values || currentId())) out = defaultResult();
    paint(out);
  }

  function onChange(e) {
    var t = e.target;
    if (!t || !t.name) return;
    if (t.name === 'id' || t.name.indexOf('option') === 0) {
      setTimeout(nativeSync, 50);
    }
  }

  var mode = null;

  function startNative() {
    if (mode) return;
    mode = 'native';
    document.addEventListener('change', onChange, { passive: true });
    window.addEventListener('popstate', nativeSync);
    nativeSync();
  }

  function startOptis() {
    if (mode === 'optis') return;
    mode = 'optis';
    optisSync();
    setInterval(optisSync, CONFIG.pollMs);
  }

  /* ======================================================================
     10. ARBITER
     ====================================================================== */

  function optisLikely() {
    if (typeof BSS_PO !== 'undefined') return true;
    for (var i = 0; i < CONFIG.optisMarkers.length; i++) {
      try {
        if (document.querySelector(CONFIG.optisMarkers[i])) return true;
      } catch (e) { /* bad selector, skip */ }
    }
    return false;
  }

  function optisReady() {
    if (typeof BSS_PO === 'undefined') return false;
    try {
      var c = BSS_PO.currentSelectedOptions;
      if (c && c.allValues && c.allValues.length) return true;

      var aos = BSS_PO.appliedOptionSet;
      if (!aos) return false;
      if (Array.isArray(aos)) return aos.length > 0;
      if (typeof aos === 'object') { for (var k in aos) return true; return false; }
      return !!aos;
    } catch (e) {
      return false;
    }
  }

  if (!optisLikely()) {
    /* No sign of Optis on this page — paint natively, immediately. */
    startNative();
  } else {
    /* Optis is probably here. Hold the block until BSS_PO can answer,
       then fall back to native if it never does. */
    root.classList.add('is-swapping');
    var startedAt = Date.now();

    (function waitForOptis() {
      if (optisReady()) { startOptis(); return; }
      if (Date.now() - startedAt >= CONFIG.optisWaitMs) { startNative(); return; }
      setTimeout(waitForOptis, 25);
    })();
  }
})();